// adminportal/src/components/DualPaneFileBrowser.tsx
import React, { useState, useEffect } from 'react';
import {
  Folder,
  File,
  Download,
  Upload,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  Home,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Trash2,
} from 'lucide-react';
import { apiUrl } from '../config';

// --- helpers ---
const trimSlashes = (s: string) => s.replace(/^\/+/, '').replace(/\/+$/, '');

const toRelative = (full: string, basePrefix: string) => {
  const base = trimSlashes(basePrefix || '');
  const f = trimSlashes(full || '');
  if (!base) return f;
  return f.startsWith(base) ? f.slice(base.length) : f;
};

// `StartDirectoryListing` and `StartFileTransfer` want /bucket/prefix form
const toS3Path = (bucket: string, prefix = '') =>
  `/${trimSlashes(bucket)}/${trimSlashes(prefix) ? `${trimSlashes(prefix)}/` : ''}`;

interface FileItem {
  name: string;
  type: 'file' | 'directory';
  size?: number;
  modified?: string;
  path: string;
}

interface DualPaneFileBrowserProps {
  connectorId: string;
  connectorName: string;
  companyId: string;
  s3Bucket: string;
  s3Prefix: string;
  onClose: () => void;
}

export default function DualPaneFileBrowser({
  connectorId,
  connectorName,
  companyId,
  s3Bucket,
  s3Prefix,
  onClose,
}: DualPaneFileBrowserProps) {
  const [leftPath, setLeftPath] = useState('/');
  const [rightPath, setRightPath] = useState(s3Prefix);

  // Navigation helpers
  const getParentPath = (currentPath: string) => {
    if (currentPath === s3Prefix) {
      // If we're at the connector's default folder, go up to company level
      const pathParts = s3Prefix.split('/');
      return `${pathParts.slice(0, -2).join('/')}/`; // Remove last two parts (outbound/)
    }
    const pathParts = currentPath.split('/');
    return `${pathParts.slice(0, -1).join('/')}/`;
  };

  const canNavigateUp = (currentPath: string) => {
    // Can navigate up if we're not at the root company level
    const companyLevel = `${s3Prefix.split('/').slice(0, -2).join('/')}/`;
    return currentPath !== companyLevel;
  };

  // DualPaneFileBrowser loaded
  const [leftFiles, setLeftFiles] = useState<FileItem[]>([]);
  const [rightFiles, setRightFiles] = useState<FileItem[]>([]);
  const [leftLoading, setLeftLoading] = useState(false);
  const [rightLoading, setRightLoading] = useState(false);
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  const [selectedRight, setSelectedRight] = useState<string | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [remoteConnectionError, setRemoteConnectionError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [dragOver, setDragOver] = useState<'left' | 'right' | null>(null);

  useEffect(() => {
    // Debug localStorage on component load
    console.log('🔍 Component loaded - localStorage keys:', Object.keys(localStorage));
    console.log(
      '🔍 Cognito-related keys:',
      Object.keys(localStorage).filter(key => key.includes('cognito') || key.includes('token'))
    );
    console.log('🔍 Raw cognito_access_token:', localStorage.getItem('cognito_access_token'));

    loadLeftFiles();
    loadRightFiles();
  }, [leftPath, rightPath]);

  const loadLeftFiles = async () => {
    console.log('🚀 SFTP Coming Soon - showing empty state');

    setLeftLoading(true);
    setRemoteConnectionError(null);

    // Show empty state - no files, just the Coming Soon message
    setLeftFiles([]);
    setLeftLoading(false);
  };

  const loadRightFiles = async () => {
    setRightLoading(true);
    setError(null);
    try {
      console.log('🔍 Loading S3 files from path:', rightPath);
      console.log('🔍 S3 prefix:', s3Prefix);

      // For right pane, we want to list LOCAL S3 files
      // Calculate the relative path from the connector's s3Prefix using the helper
      const relativePath = toRelative(rightPath, s3Prefix);
      console.log('🔍 Calculated relative path:', relativePath);

      const requestBody = {
        connectorId,
        remoteDirectoryPath: '', // Empty for S3 listing
        outputDirectoryPath: relativePath,
      };

      console.log('🔍 Request body:', requestBody);

      // Loading S3 directory
      const response = await fetch(
        apiUrl(`sftp-connectors/${connectorId}/operations/directory-listing`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        }
      );

      console.log('🔍 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ API Error:', errorData);
        throw new Error(errorData.message || 'Failed to load S3 files');
      }

      const data = await response.json();
      console.log('✅ S3 files loaded:', data);
      setRightFiles(data.files || []);
    } catch (err: any) {
      console.error('❌ Error loading S3 files:', err);
      setError(err.message);
      setRightFiles([]);
    } finally {
      setRightLoading(false);
    }
  };

  const handleLeftClick = (file: FileItem) => {
    if (file.type === 'directory') {
      // Navigate to the folder
      setLeftPath(file.path);
      setSelectedLeft(null);
    } else {
      // Select the file
      setSelectedLeft(file.path);
    }
  };

  const handleRightClick = (file: FileItem) => {
    if (file.type === 'directory') {
      // Use the file.path directly as it already contains the full path from the backend
      setRightPath(file.path);
      setSelectedRight(null);
    } else {
      // Use the file.path directly as it already contains the full path from the backend
      setSelectedRight(file.path);
    }
  };

  const handleLeftNavigateUp = () => {
    const parentPath = getParentPath(leftPath);
    setLeftPath(parentPath);
    setSelectedLeft(null);
  };

  const handleNavigateUp = () => {
    const parentPath = getParentPath(rightPath);
    setRightPath(parentPath);
    setSelectedRight(null);
  };

  const handleNavigateToConnectorFolder = () => {
    setRightPath(s3Prefix);
    setSelectedRight(null);
  };

  const handleConnectToRemote = async () => {
    setIsConnecting(true);
    setRemoteConnectionError(null);
    try {
      // Try to test the connection first using the correct endpoint
      const testResponse = await fetch(
        apiUrl(`sftp-connectors/${connectorId}/operations/test-connection`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ connectorId }),
        }
      );

      if (testResponse.ok) {
        // If test connection succeeds, try to load files
        await loadLeftFiles();
      } else {
        const errorData = await testResponse.json().catch(() => ({}));
        setRemoteConnectionError(errorData.message || 'Connection test failed');
      }
    } catch (err: any) {
      setRemoteConnectionError(err.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleFileAction = async (file: FileItem, action: 'download' | 'upload') => {
    if (action === 'download') {
      // Download from remote SFTP to local S3
      setSelectedLeft(file.path);
      setSelectedRight(null);
      await transferFile('download');
    } else {
      // Upload from local S3 to remote SFTP
      setSelectedRight(file.path);
      setSelectedLeft(null);
      await transferFile('upload');
    }
  };

  const handleDeleteFile = async (file: FileItem, side: 'left' | 'right') => {
    if (!confirm(`Are you sure you want to delete "${file.name}"?`)) {
      return;
    }

    try {
      if (side === 'right') {
        // getAccessToken() should pull from Cognito service. If you have a refresh flow, call it once here.
        const token = localStorage.getItem('cognito_access_token');

        // TODO: Add refresh logic if you have a silent refresh helper
        // if (!token && refreshSessionSilently) {
        //   try { await refreshSessionSilently(); token = localStorage.getItem('cognito_access_token'); } catch {}
        // }

        if (!token) {
          console.warn('🔍 Access token for delete: Missing');
          setError('Session expired. Please sign in again.');
          // TODO: Add navigation to login if you have a router
          // navigate('/login');
          return;
        }

        // Proceed with delete
        const delRes = await fetch(apiUrl('files/delete'), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            filePath: file.path, // Use the correct field name the API expects
          }),
        });

        if (!delRes.ok) {
          const text = await delRes.text();
          console.error('❌ Error deleting file:', text);
          setError('Delete failed.');
        } else {
          console.log('✅ File deleted successfully');
          // refresh the listing
          await loadRightFiles();
        }
      } else {
        // Delete from remote SFTP (if supported)
        console.log('Remote file deletion not yet implemented');
        setError('Remote file deletion is not yet implemented');
      }
    } catch (err: any) {
      console.error('❌ Error deleting file:', err);
      setError(err.message);
    }
  };

  const handleDragStart = (e: React.DragEvent, file: FileItem, source: 'left' | 'right') => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ file, source }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, target: 'left' | 'right') => {
    e.preventDefault();

    // Check if this is a file drop from outside
    const hasFiles = e.dataTransfer.types.includes('Files');
    if (hasFiles) {
      e.dataTransfer.dropEffect = 'copy'; // Show copy cursor for external files
    } else {
      e.dataTransfer.dropEffect = 'move'; // Show move cursor for internal drag
    }

    setDragOver(target);
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleDrop = async (e: React.DragEvent, target: 'left' | 'right') => {
    e.preventDefault();
    setDragOver(null);

    try {
      // Check if this is a file drop from outside (like desktop)
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        console.log('📁 External file drop detected:', files.length, 'files');

        // Handle external file upload
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          console.log('📄 Uploading external file:', file.name);

          // Create a fake event for the file upload handler
          const fakeEvent = {
            target: { files: [file], value: '' },
            nativeEvent: new Event('change'),
            currentTarget: null,
            bubbles: false,
            cancelable: false,
            defaultPrevented: false,
            eventPhase: 0,
            isTrusted: false,
            preventDefault: () => {},
            isDefaultPrevented: () => false,
            stopPropagation: () => {},
            isPropagationStopped: () => false,
            persist: () => {},
            timeStamp: Date.now(),
            type: 'change',
          } as unknown as React.ChangeEvent<HTMLInputElement>;

          // Upload to the appropriate destination
          const destination = target === 'left' ? 'sftp' : 's3';
          await handleFileUpload(fakeEvent, destination);
        }
        return;
      }

      // Check for internal drag and drop data
      const dataString = e.dataTransfer.getData('text/plain');

      // Check if we have valid data
      if (!dataString || dataString.trim() === '') {
        console.log('No drag data available - ignoring drop');
        return;
      }

      const data = JSON.parse(dataString);
      const { file, source } = data;

      if (!file || !source) {
        console.log('Invalid drag data - ignoring drop');
        return;
      }

      if (source === target) return; // Can't drop on same pane

      if (source === 'left' && target === 'right') {
        // Download from remote to local
        setSelectedLeft(file.path);
        setSelectedRight(null);
        await transferFile('download');
      } else if (source === 'right' && target === 'left') {
        // Upload from local to remote
        setSelectedRight(file.path);
        setSelectedLeft(null);
        await transferFile('upload');
      }
    } catch (err) {
      console.error('Drag and drop error:', err);
      // Don't show error to user for drag/drop issues
    }
  };

  const transferFile = async (direction: 'download' | 'upload') => {
    if (!selectedLeft && !selectedRight) return;

    setTransferring(true);
    setError(null);

    try {
      const response = await fetch(
        apiUrl(`sftp-connectors/${connectorId}/operations/file-transfer`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            connectorId,
            retrieveFilePaths: direction === 'download' ? [selectedLeft!] : undefined,
            sendFilePaths: direction === 'upload' ? [selectedRight!] : undefined,
            localDirectoryPath: rightPath,
            remoteDirectoryPath: leftPath,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Transfer failed');
      }

      // Refresh both sides after transfer
      await Promise.all([loadLeftFiles(), loadRightFiles()]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTransferring(false);
    }
  };

  const navigateUp = (side: 'left' | 'right') => {
    if (side === 'left') {
      const parentPath = leftPath.split('/').slice(0, -1).join('/') || '/';
      setLeftPath(parentPath);
    } else {
      // Use the new navigation logic for right side
      handleNavigateUp();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    destination: 's3' | 'sftp'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      if (destination === 's3') {
        // Check file size - use presigned upload for files > 5MB
        const fileSizeMB = file.size / (1024 * 1024);

        if (fileSizeMB > 5) {
          // Use presigned upload for large files
          console.log(
            `📤 Large file detected (${fileSizeMB.toFixed(2)}MB), using presigned upload`
          );

          const presignedResponse = await fetch(apiUrl('files/presigned-upload'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              path: rightPath,
              bucket: s3Bucket,
            }),
          });

          if (!presignedResponse.ok) {
            throw new Error('Failed to get presigned URL');
          }

          const { uploadUrl, key } = await presignedResponse.json();

          // Upload directly to S3 using presigned URL
          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          });

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload to S3');
          }

          console.log('✅ Large file uploaded successfully via presigned URL');
        } else {
          // Use direct upload for small files
          console.log(`📤 Small file detected (${fileSizeMB.toFixed(2)}MB), using direct upload`);

          const formData = new FormData();
          formData.append('file', file);
          formData.append('path', rightPath);
          formData.append('bucket', s3Bucket);
          formData.append('destination', 's3');

          const response = await fetch(apiUrl('upload-file'), {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            throw new Error('Failed to upload to S3');
          }
        }

        // Refresh right side
        await loadRightFiles();
      } else {
        // Upload to SFTP - First upload to S3, then transfer to SFTP
        // Step 1: Upload to S3 staging area
        const fileSizeMB = file.size / (1024 * 1024);
        let s3Result;

        if (fileSizeMB > 5) {
          // Use presigned upload for large files
          console.log(
            `📤 Large file detected (${fileSizeMB.toFixed(2)}MB), using presigned upload for SFTP`
          );

          const presignedResponse = await fetch(apiUrl('files/presigned-upload'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              path: rightPath,
              bucket: s3Bucket,
            }),
          });

          if (!presignedResponse.ok) {
            throw new Error('Failed to get presigned URL');
          }

          const { uploadUrl, key } = await presignedResponse.json();

          // Upload directly to S3 using presigned URL
          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          });

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload to S3');
          }

          s3Result = { key, message: 'File uploaded successfully' };
          console.log('✅ Large file uploaded to S3 via presigned URL:', s3Result);
        } else {
          // Use presigned URL for all files (more reliable)
          console.log(`📤 Using presigned URL upload for file (${fileSizeMB.toFixed(2)}MB)`);

          // Get the access token from localStorage
          const accessToken = localStorage.getItem('cognito_access_token');
          if (!accessToken) {
            throw new Error('No access token found. Please log in again.');
          }

          // Get presigned URL
          const presignedResponse = await fetch(apiUrl('files/presigned-upload'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              fileName: file.name,
              fileType: file.type,
              filePath: rightPath,
              fileSize: file.size,
            }),
          });

          if (!presignedResponse.ok) {
            const errorData = await presignedResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to get presigned URL');
          }

          const { presignedUrl, s3Key, bucketName } = await presignedResponse.json();

          // Upload directly to S3 using presigned URL (binary-safe, no corruption)
          const s3Response = await fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
            },
          });

          if (!s3Response.ok) {
            throw new Error(`S3 upload failed: ${s3Response.status} ${s3Response.statusText}`);
          }

          s3Result = {
            success: true,
            message: 'File uploaded successfully',
            s3Key,
            bucketName,
            fileName: file.name,
            fileSize: file.size,
            file: {
              path: s3Key,
              name: file.name,
              size: file.size,
              type: file.type,
            },
          };
          console.log('✅ File uploaded to S3:', s3Result);
        }

        // Ensure the file path starts with / for Transfer Family
        const filePath = s3Result.file?.path?.startsWith('/')
          ? s3Result.file.path
          : `/${s3Result.file?.path || ''}`;

        console.log('🔍 Using file path for transfer:', filePath);

        // Step 2: Transfer from S3 to SFTP using the file transfer API
        const transferResponse = await fetch(
          apiUrl(`sftp-connectors/${connectorId}/operations/file-transfer`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              connectorId,
              sendFilePaths: [filePath], // Use the properly formatted path
              localDirectoryPath: rightPath,
              remoteDirectoryPath: leftPath,
            }),
          }
        );

        if (!transferResponse.ok) {
          const errorData = await transferResponse.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to transfer to SFTP');
        }

        const transferResult = await transferResponse.json();
        console.log('✅ File transferred to SFTP:', transferResult);

        // Refresh both sides
        await Promise.all([loadLeftFiles(), loadRightFiles()]);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const FileIcon = ({ file }: { file: FileItem }) =>
    file.type === 'directory' ? (
      <Folder className='w-4 h-4 text-blue-400' />
    ) : (
      <File className='w-4 h-4 text-slate-400' />
    );

  const FileList = ({
    files,
    loading,
    selected,
    onFileClick,
    path,
    onNavigateUp,
    title,
    connectionError,
    onConnect,
    isConnecting,
    paneType,
  }: {
    files: FileItem[];
    loading: boolean;
    selected: string | null;
    onFileClick: (file: FileItem) => void;
    path: string;
    onNavigateUp: () => void;
    title: string;
    connectionError?: string | null;
    onConnect?: () => void;
    isConnecting?: boolean;
    paneType: 'left' | 'right';
  }) => (
    <div
      className={`h-full bg-slate-800 rounded-lg border border-white/10 transition-colors flex flex-col ${
        dragOver === paneType ? 'bg-blue-500/10 border-blue-500/30' : ''
      }`}
      onDragOver={e => handleDragOver(e, paneType)}
      onDragLeave={handleDragLeave}
      onDrop={e => handleDrop(e, paneType)}
    >
      <div className='p-4 border-b border-white/10 flex-shrink-0'>
        <div className='flex items-center justify-between mb-2'>
          <h3 className='font-medium text-white'>{title}</h3>
          <div className='flex items-center gap-2'>
            <input
              type='file'
              id={`upload-${title.toLowerCase().replace(/\s+/g, '-')}`}
              onChange={e => handleFileUpload(e, title.includes('Remote') ? 'sftp' : 's3')}
              className='hidden'
              disabled={uploading || paneType === 'left'}
              aria-label={`Upload file to ${title}`}
            />
            <label
              htmlFor={`upload-${title.toLowerCase().replace(/\s+/g, '-')}`}
              className={`p-1 hover:bg-slate-700 rounded transition-colors ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              } ${paneType === 'left' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              title={
                paneType === 'left'
                  ? 'Coming Soon: SFTP upload functionality is in development'
                  : 'Upload file'
              }
              onClick={
                paneType === 'left'
                  ? e => {
                      e.preventDefault();
                      alert('Coming Soon: SFTP upload functionality is in development');
                    }
                  : undefined
              }
            >
              <Upload className='w-4 h-4 text-slate-400' />
            </label>
            <button
              onClick={onNavigateUp}
              className='p-1 hover:bg-slate-700 rounded'
              title='Go up one level'
            >
              <ChevronLeft className='w-4 h-4 text-slate-400' />
            </button>
          </div>
        </div>
        <div className='text-sm text-slate-400 font-mono truncate' title={path}>
          {path}
        </div>
      </div>

      <div className='p-4 flex-1 overflow-hidden flex flex-col'>
        {loading ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='w-6 h-6 animate-spin text-blue-400' />
          </div>
        ) : connectionError ? (
          <div className='text-center py-8'>
            <div className='bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-4'>
              <div className='flex items-center justify-center mb-2'>
                <div className='w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center'>
                  <span className='text-yellow-400 text-lg'>⚠️</span>
                </div>
              </div>
              <h4 className='text-yellow-400 font-medium mb-2'>Connection Not Established</h4>
              <p className='text-yellow-300 text-sm mb-4'>
                Unable to connect to remote server. {connectionError}
              </p>
              {onConnect && (
                <button
                  onClick={onConnect}
                  disabled={isConnecting}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isConnecting
                      ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                      : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  }`}
                >
                  {isConnecting ? (
                    <div className='flex items-center gap-2'>
                      <Loader2 className='w-4 h-4 animate-spin' />
                      Connecting...
                    </div>
                  ) : (
                    'Connect Now'
                  )}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className='flex-1 overflow-y-auto space-y-1'>
            {files.map(file => (
              <div
                key={file.path}
                draggable={file.type === 'file'}
                onDragStart={e => handleDragStart(e, file, paneType)}
                onClick={() => onFileClick(file)}
                className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                  selected === file.path
                    ? 'bg-blue-600/20 border border-blue-500/30'
                    : 'hover:bg-slate-700/50'
                } ${file.type === 'file' ? 'cursor-grab active:cursor-grabbing' : ''}`}
              >
                <FileIcon file={file} />
                <span className='text-white flex-1 truncate' title={file.name}>
                  {file.name}
                </span>
                <div className='flex items-center gap-2 flex-shrink-0'>
                  {file.size && (
                    <span className='text-xs text-slate-400'>
                      {file.size > 1024 * 1024
                        ? `${(file.size / (1024 * 1024)).toFixed(1)}MB`
                        : `${(file.size / 1024).toFixed(1)}KB`}
                    </span>
                  )}
                  {file.type === 'file' && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleFileAction(file, title.includes('Remote') ? 'download' : 'upload');
                      }}
                      className='p-1 hover:bg-slate-600 rounded transition-colors'
                      title={title.includes('Remote') ? 'Download file' : 'Upload file'}
                    >
                      {title.includes('Remote') ? (
                        <Download className='w-3 h-3 text-blue-400' />
                      ) : (
                        <Upload className='w-3 h-3 text-green-400' />
                      )}
                    </button>
                  )}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteFile(file, paneType);
                    }}
                    className='p-1 hover:bg-slate-600 rounded transition-colors'
                    title='Delete file'
                  >
                    <Trash2 className='w-3 h-3 text-red-400' />
                  </button>
                </div>
              </div>
            ))}
            {files.length === 0 && !connectionError && (
              <div className='text-center py-8 text-slate-400'>No files found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
      <div className='bg-slate-900 rounded-lg w-[90vw] h-[80vh] max-w-7xl flex flex-col'>
        {/* Header */}
        <div className='p-6 border-b border-white/10'>
          <div className='flex items-center justify-between'>
            <div>
              <h2 className='text-xl font-semibold text-white'>File Browser</h2>
              <p className='text-slate-400'>
                {connectorName} - {companyId}
              </p>
            </div>
            <button
              onClick={onClose}
              className='p-2 hover:bg-slate-700 rounded-lg transition-colors'
            >
              <span className='text-white'>×</span>
            </button>
          </div>
        </div>

        {/* Navigation Breadcrumbs */}
        <div className='px-6 py-3 border-b border-white/10 bg-slate-800/50'>
          <div className='flex items-center gap-6'>
            {/* Remote Server Breadcrumbs */}
            <div className='flex items-center gap-2 text-sm'>
              <span className='text-slate-400'>Remote Server:</span>
              <div className='flex items-center gap-1'>
                {leftPath === '/' ? (
                  <button
                    onClick={() => {
                      setLeftPath('/');
                      setSelectedLeft(null);
                    }}
                    className='px-2 py-1 rounded transition-colors bg-blue-600 text-white'
                  >
                    /
                  </button>
                ) : (
                  leftPath
                    .split('/')
                    .filter(Boolean)
                    .map((part: string, index: number, array: string[]) => {
                      const pathUpToHere = `${array.slice(0, index + 1).join('/')}/`;
                      const isLast = index === array.length - 1;
                      return (
                        <div key={index} className='flex items-center gap-1'>
                          {index > 0 && <ChevronRight className='w-3 h-3 text-slate-500' />}
                          <button
                            onClick={() => {
                              setLeftPath(pathUpToHere);
                              setSelectedLeft(null);
                            }}
                            className={`px-2 py-1 rounded transition-colors ${
                              isLast
                                ? 'bg-blue-600 text-white'
                                : 'text-slate-300 hover:bg-slate-700'
                            }`}
                          >
                            {part}
                          </button>
                        </div>
                      );
                    })
                )}
              </div>
            </div>

            {/* Local Storage Breadcrumbs */}
            <div className='flex items-center gap-2 text-sm'>
              <span className='text-slate-400'>Local Storage:</span>
              <div className='flex items-center gap-1'>
                {rightPath
                  .split('/')
                  .filter(Boolean)
                  .map((part, index, array) => {
                    const pathUpToHere = `${array.slice(0, index + 1).join('/')}/`;
                    const isLast = index === array.length - 1;
                    return (
                      <div key={index} className='flex items-center gap-1'>
                        {index > 0 && <ChevronRight className='w-3 h-3 text-slate-500' />}
                        <button
                          onClick={() => {
                            setRightPath(pathUpToHere);
                            setSelectedRight(null);
                          }}
                          className={`px-2 py-1 rounded transition-colors ${
                            isLast ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
                          }`}
                        >
                          {part}
                        </button>
                      </div>
                    );
                  })}
              </div>
              {rightPath !== s3Prefix && (
                <button
                  onClick={handleNavigateToConnectorFolder}
                  className='ml-2 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded transition-colors'
                >
                  Back to Connector Folder
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className='mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg'>
            <p className='text-red-400 text-sm'>{error}</p>
          </div>
        )}

        {/* Main Content */}
        <div className='flex-1 p-6 flex gap-6 min-h-0'>
          {/* Left Pane - Remote SFTP */}
          <div className='flex-1 min-h-0 relative'>
            <FileList
              files={leftFiles}
              loading={leftLoading}
              selected={selectedLeft}
              onFileClick={handleLeftClick}
              path={leftPath}
              onNavigateUp={() => navigateUp('left')}
              title='Remote Server (SFTP)'
              connectionError={remoteConnectionError}
              onConnect={handleConnectToRemote}
              isConnecting={isConnecting}
              paneType='left'
            />

            {/* Coming Soon Message - Overlay */}
            <div className='absolute inset-0 bg-slate-800/95 backdrop-blur-sm rounded-lg border border-blue-500/20 flex items-center justify-center z-10'>
              <div className='p-6 bg-blue-500/10 border border-blue-500/20 rounded-lg max-w-md text-center'>
                <div className='flex items-center justify-center mb-4'>
                  <div className='text-4xl'>🚧</div>
                </div>
                <h3 className='text-blue-400 font-semibold text-lg mb-2'>Coming Soon</h3>
                <p className='text-blue-300 text-sm'>
                  SFTP file browsing is currently in development. We're working to enhance the file
                  transfer capabilities via web interface.
                </p>
              </div>
            </div>
          </div>

          {/* Transfer Controls */}
          <div className='flex flex-col items-center justify-center gap-4 px-4'>
            <div className='text-center'>
              <button
                onClick={() => transferFile('download')}
                disabled={!selectedLeft || transferring}
                className='p-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg transition-colors'
                title='Download from remote to local'
              >
                <Download className='w-5 h-5 text-white' />
              </button>
              <div className='text-xs text-slate-400 mt-1'>Download</div>
            </div>

            <div className='text-center'>
              <button
                onClick={() => transferFile('upload')}
                disabled={!selectedRight || transferring}
                className='p-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg transition-colors'
                title='Upload from local to remote'
              >
                <Upload className='w-5 h-5 text-white' />
              </button>
              <div className='text-xs text-slate-400 mt-1'>Upload</div>
            </div>

            {transferring && <Loader2 className='w-6 h-6 animate-spin text-blue-400' />}
            {uploading && (
              <div className='text-center'>
                <Loader2 className='w-4 h-4 animate-spin text-green-400 mx-auto mb-1' />
                <span className='text-xs text-green-400'>Uploading...</span>
              </div>
            )}
          </div>

          {/* Right Pane - Local S3 */}
          <div className='flex-1 min-h-0'>
            <FileList
              files={rightFiles}
              loading={rightLoading}
              selected={selectedRight}
              onFileClick={handleRightClick}
              path={rightPath}
              onNavigateUp={() => navigateUp('right')}
              title='Local Storage (S3)'
              paneType='right'
            />
          </div>
        </div>

        {/* Footer */}
        <div className='p-6 border-t border-white/10'>
          <div className='flex items-center justify-between text-sm text-slate-400'>
            <div>
              Selected:{' '}
              {selectedLeft
                ? `Remote: ${selectedLeft}`
                : selectedRight
                  ? `Local: ${selectedRight}`
                  : 'None'}
            </div>
            <div className='flex gap-4'>
              <button
                onClick={() => Promise.all([loadLeftFiles(), loadRightFiles()])}
                className='flex items-center gap-2 px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded transition-colors'
              >
                <RefreshCw className='w-4 h-4' />
                Refresh All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
